import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Pet from '@spike/pet-model';
import Staff from 'model/Staff';
import { Moment } from 'moment-timezone';
import AddOrEditService from './AddOrEditService';
import CreateBookingHeader from '../CreateBookingHeader';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import FooterComponent from '../FooterComponent';
import { PetServiceWrapper, versionPetAndServices, Discount } from 'model/NewMultipleBooking';
import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE } from 'constants/index';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { RootState } from 'store';

interface Props {
    parentId: number;
    petId?: number;
    service?: PetServiceWrapper;
    staffId?: number;
    staffIdPreselected?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    petsUsed?: Array<number>;
    servicesUsed?: Array<number>;
    discount: Discount;
    loading?: boolean;
    petWithoutSelect?: boolean;
    hideRemove?: boolean;
    onBack: () => void;
    onBook: (
        dateAppt?: Moment,
        petIdAppt?: number,
        serviceAppt?: PetServiceWrapper,
        staffIdAppt?: number,
        discountAppt?: Discount
    ) => void;
    onRemove?: (petId: number | undefined, service: PetServiceWrapper | undefined) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        petContainer: {
            marginTop: 8,
            marginBottom: 8
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4',
            marginTop: 11,
            marginBottom: 6
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '17px 27px 11px 27px',
            [theme.breakpoints.up('md')]: {
                margin: '20px 30px 13px 30px'
            }
        },
        content: {
            margin: '0px 26px 0px 26px',
            [theme.breakpoints.up('md')]: {
                margin: '0px 30px 0px 30px'
            }
        },
        addService: {
            cursor: 'pointer',
            color: '#92B4A7',
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '27px',
            paddingTop: 7
        }
    })
);

export const PetAndServices: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const [service, setService] = useState<PetServiceWrapper | undefined>(props.service);
    const [staffId, setStaffId] = useState<number | undefined>(props.staffId);
    const [date, setDate] = useState<Moment | undefined>(props.date);
    const [discount, setDiscount] = useState<Discount>(props.discount);
    const [petId, setPetId] = useState<number | undefined>(props.petId);
    const [defaultDiscount, setDefaultDiscount] = useState<string | undefined>(undefined);

    const getTitle = () => {
        if (props.versionPetAndServices === versionPetAndServices.add) {
            return 'Add Service';
        } else if (props.versionPetAndServices === versionPetAndServices.edit) {
            return 'Edit Service';
        } else {
            return 'Pet & Services';
        }
    };

    const isDisabledButton = () => {
        if (props.versionPetAndServices !== versionPetAndServices.edit) {
            if (!props.parentId || !petId || !service || !staffId || isNaN(Number(discount?.value))) {
                return true;
            }
            return false;
        } else {
            if (!petId || !service || !staffId) {
                return true;
            }
            return false;
        }
    };

    const handlerBook = () => {
        props.onBook(date, petId, service, staffId, discount);
    };

    const handleRemoveAppointment = () => {
        props.onRemove && props.onRemove(props.petId, props.service);
    };

    const handlerBack = () => {
        setDate(props.date);
        setService(undefined);
        setStaffId(undefined);
        props.onBack();
    };

    const handlerPet = (value: Pet) => {
        setPetId(value.id);
        amplitude.track(AMPLITUDE.LOAD_DATE_AND_TIME);
        amplitude.track(AMPLITUDE.LOAD_SERVICES);
    };

    const handleChangeService = (value: PetServiceWrapper, data: boolean) => {
        setService(value);
        if (!data) {
            setStaffId(undefined);
        }
        setDiscount(prev => ({
            ...prev,
            type: 'amount',
            value: 0
        }));
        setDefaultDiscount('0.00');
        amplitude.track(AMPLITUDE.LOAD_STAFF);
    };

    const handlerStaff = (staffSelected?: Staff) => {
        setStaffId(staffSelected?.id);
        amplitude.track(AMPLITUDE.LOAD_COLLECT_DEPOSIT_PAYMENT_METHOD);
        amplitude.track(AMPLITUDE.LOAD_FINAL_INFORMATION);
    };

    const handlerChangeDiscount = (value: Discount) => {
        setDiscount(prev => ({
            ...prev,
            type: value?.type,
            value: value?.value
        }));
    };

    useEffect(() => {
        if (petId !== props.petId) {
            setDate(props.date);
            setService(undefined);
            setStaffId(undefined);
            setDiscount(prev => ({
                ...prev,
                type: 'amount',
                value: 0
            }));
            setDefaultDiscount('0.00');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    useEffect(() => {
        if (staffs?.length === 0) {
            dispatch(fetchStaffThunk());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffs]);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultDiscount) {
                setDefaultDiscount(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [defaultDiscount]);

    return (
        <>
            <Box className={classes.headerContainer}>
                <CreateBookingHeader
                    title={getTitle()}
                    onBack={handlerBack}
                />
            </Box>
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <Box className={classes.content}>
                    <AddOrEditService
                        parentId={props.parentId!}
                        petId={petId}
                        service={service}
                        staffId={staffId}
                        staffIdPreselected={props.staffIdPreselected}
                        petsUsed={props.petsUsed}
                        servicesUsed={props.servicesUsed}
                        versionPetAndServices={props.versionPetAndServices}
                        discount={discount}
                        date={date}
                        petWithoutSelect={props.petWithoutSelect || false}
                        defaultDiscount={defaultDiscount}
                        onChangePet={handlerPet}
                        onChangeDate={setDate}
                        onChangeService={handleChangeService}
                        onChangeStaff={handlerStaff}
                        onChangeDiscount={handlerChangeDiscount}
                    />
                </Box>
            </OverlayScrollbarsComponent>
            <Box className={classes.totalContainer}>
                <FooterComponent
                    title="Total"
                    loading={props.loading || false}
                    hours={service?.duration?.hours ?? props.service?.duration?.hours}
                    minutes={service?.duration?.minutes ?? props.service?.duration?.minutes}
                    buttonText="Save"
                    disabledButton={isDisabledButton()}
                    onBook={handlerBook}
                    onRemove={
                        props.versionPetAndServices === versionPetAndServices.edit && !props.hideRemove
                            ? handleRemoveAppointment
                            : undefined
                    }
                />
            </Box>
        </>
    );
};

export default PetAndServices;
